import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Modal from '@components/common/Modal/Modal';
import Button from '@components/Button/Button';
import Select from '@components/Form/Select/Select';
import mentorsService from '@/services/mentors/mentorsService';
import evaluationsService, { type TemplateScope } from '@/services/evaluations/evaluationsService';
import JudgeSelect from './JudgeSelect';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** The cohort currently in view — enables the "this cohort" scope. */
    cohortId?: string;
    cohortName?: string;
}

const DownloadTemplateModal: React.FC<Props> = ({ isOpen, onClose, cohortId, cohortName }) => {
    const { t } = useTranslation('evaluation');

    const [scope, setScope] = useState<TemplateScope>(cohortId ? 'cohort' : 'all');
    const [mentorId, setMentorId] = useState('');
    const [judgeId, setJudgeId] = useState('');
    const [limitToCohort, setLimitToCohort] = useState(true);
    const [busy, setBusy] = useState(false);

    const { data: mentorsRes } = useQuery({
        queryKey: ['evaluation-mentors'],
        queryFn: () => mentorsService.getMentors(1, 300),
        enabled: isOpen,
        staleTime: 5 * 60 * 1000,
    });

    const mentorOptions = useMemo(
        () => (mentorsRes?.data?.data ?? []).map((m) => ({ value: m.id, label: m.name })),
        [mentorsRes],
    );

    const scopeChoices: { value: TemplateScope; label: string }[] = [
        { value: 'all', label: t('download.scopeAll') },
        ...(cohortId
            ? [{ value: 'cohort' as TemplateScope, label: t('download.scopeCohort', { name: cohortName ?? '' }) }]
            : []),
        { value: 'mentor', label: t('download.scopeMentor') },
    ];

    const handleDownload = async () => {
        if (scope === 'mentor' && !mentorId) {
            toast.error(t('download.needMentor'));
            return;
        }
        if (!judgeId) {
            toast.error(t('judge.required'));
            return;
        }
        setBusy(true);
        try {
            await evaluationsService.downloadTemplate({
                scope,
                judge_id: judgeId,
                cohort_id:
                    scope === 'cohort'
                        ? cohortId
                        : scope === 'mentor' && limitToCohort && cohortId
                            ? cohortId
                            : undefined,
                mentor_id: scope === 'mentor' ? mentorId : undefined,
            });
            toast.success(t('download.done'));
            onClose();
        } catch (err: any) {
            toast.error(err?.message || t('download.failed'));
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen onClose={onClose} title={t('download.title')} maxWidth="max-w-[520px]">
            <div className="p-5 space-y-4">
                <p className="text-xs text-gray-500">{t('download.intro')}</p>

                <JudgeSelect value={judgeId} onChange={setJudgeId} required />

                <div>
                    <div className="text-xs font-semibold text-gray-700 mb-2">{t('download.scope')}</div>
                    <div className="space-y-2">
                        {scopeChoices.map((c) => (
                            <label key={c.value} className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="eval-template-scope"
                                    checked={scope === c.value}
                                    onChange={() => setScope(c.value)}
                                    className="mt-0.5"
                                />
                                <span className="text-sm text-gray-700">{c.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {scope === 'mentor' && (
                    <div className="space-y-2">
                        <Select
                            label={t('download.mentor')}
                            placeholder={t('download.mentorPlaceholder')}
                            options={mentorOptions}
                            value={mentorId}
                            onChange={(v) => setMentorId(v || '')}
                            isClearable={false}
                        />
                        {cohortId && (
                            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-600">
                                <input
                                    type="checkbox"
                                    checked={limitToCohort}
                                    onChange={(e) => setLimitToCohort(e.target.checked)}
                                />
                                {t('download.limitToCohort', { name: cohortName ?? '' })}
                            </label>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={busy}>
                        {t('download.cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleDownload} isLoading={busy} disabled={!judgeId || busy}>
                        {busy ? t('download.generating') : t('download.download')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default DownloadTemplateModal;
